/**
 * Resolving a finding or a gap, two ways.
 *
 * - `ResolveFindingForm`: the finding page's compact form, in a popover under
 *   the top bar's Resolve. One choice (a newer version, the current version
 *   with a note, or an upload; for a missing document, a document since
 *   uploaded or an upload), the requirement inline, and a confirm button that
 *   names what it records ("Resolve with File v5"). Inspector's fix is not
 *   here: Apply fix is the page's own primary action.
 * - `ResolveRecommendationDialog`: the policy page's dialog for an amendment
 *   or a missing document, with Inspector's proposal as text (its PDF pages
 *   only on request), an upload, or a version named.
 *
 * Every path records its version (or document) and the reviewer's note.
 */
import type { DocumentChangesView } from "@repo/shared/findingChanges";
import { FileSearchIcon, FileUpIcon, LoaderCircleIcon } from "lucide-react";
import { useRef, useState, type ReactNode } from "react";

import { cn } from "~/lib/utils";
import { usePolicyVersions, type Recommendation } from "../../../state/governance";
import { formatBytes, policyDisplayName, usePolicies } from "../../../state/policies";
import { Button } from "../../ui/button";
import {
  Dialog,
  DialogClose,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogPanel,
  DialogPopup,
  DialogTitle,
} from "../../ui/dialog";
import {
  AnimatedTabs,
  AnimatedTabsContent,
  AnimatedTabsList,
  AnimatedTabsTrigger,
} from "../../ui/animated-tabs";
import { Radio, RadioGroup } from "../../ui/radio-group";
import { Select, SelectContent, SelectItem, SelectTrigger } from "../../ui/select";
import { Textarea } from "../../ui/textarea";
import { FindingChangesSkeleton } from "../LoadingSkeletons";
import { createResolutionUpload } from "./resolutionUpload";
import { formatDate } from "./PolicyGovernanceTabs";
import { FindingPdfChangesReview } from "../finding/changes/FindingChangesPanel";
import { InlineDiff } from "../finding/changes/InlineDiff";
import { editKind, pageLabel } from "../finding/changes/proposedFix";
import { proposalDecisionNote, useApplyProposal } from "../finding/changes/useApplyProposal";
import {
  defaultResolveChoice,
  resolveBlocker,
  resolveChoices,
  resolveConfirmLabel,
  versionName,
  type ResolveChoice,
  type ResolveTarget,
} from "../finding/resolveControl";
import { useFindingChanges } from "../../../state/findingChanges";
import {
  assessmentBaselineKnown,
  coveredNoteProblem,
  resolutionCandidates,
  type AssessedStamp,
  type VersionCandidate,
} from "./resolutionCandidates";

export interface ResolutionEvidence {
  readonly resolvedVersion?: number;
  readonly resolvedPolicyId?: string;
  readonly note: string | null;
}

type Mode = "upload" | "existing" | "proposal";

/**
 * Reserve space for the upload and existing-version forms. A dialog with a
 * PDF review also bounds the entire popup so long reviews scroll inside it.
 */
// 16.5rem is the upload pane's own height (drop zone + two textareas) with
// a little air; measured, because a guess here showed up as a 10px jump.
const VERSION_DIALOG_CLASS =
  "h-[min(640px,85dvh)] max-w-5xl [&_[data-slot=dialog-header]]:shrink-0 [&_[data-slot=dialog-footer]]:shrink-0";

const PANE_CLASS = "min-h-[16.5rem] pt-1 [&>div]:space-y-4";

function FilePicker({
  file,
  onChange,
  hint,
  disabled,
  compact = false,
}: {
  readonly file: File | null;
  readonly onChange: (file: File | null) => void;
  readonly hint: string;
  readonly disabled: boolean;
  /** The resolve popover's size: one line, a small button. */
  readonly compact?: boolean;
}) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [over, setOver] = useState(false);
  return (
    <div
      onDragOver={(event) => {
        event.preventDefault();
        if (disabled) return;
        setOver(true);
      }}
      onDragLeave={() => setOver(false)}
      onDrop={(event) => {
        event.preventDefault();
        setOver(false);
        if (disabled) return;
        onChange(event.dataTransfer.files?.[0] ?? null);
      }}
      className={cn(
        "flex items-center border border-dashed transition-colors",
        compact ? "gap-2.5 rounded-lg px-3 py-2" : "gap-3 rounded-xl px-4 py-3",
        over ? "border-foreground/40 bg-muted/60" : "border-border bg-muted/20",
      )}
      data-file-picker=""
    >
      <input
        ref={inputRef}
        type="file"
        disabled={disabled}
        accept="application/pdf,.pdf"
        className="hidden"
        onChange={(event) => onChange(event.target.files?.[0] ?? null)}
      />
      <FileUpIcon className={cn("shrink-0 text-muted-foreground", compact ? "size-4" : "size-5")} />
      <span className="min-w-0 flex-1">
        {file === null ? (
          <>
            <span className={cn("block font-medium text-foreground", compact ? "text-[13px]" : "text-sm")}>Drop the PDF here</span>
            <span className="block truncate text-xs text-muted-foreground">{hint}</span>
          </>
        ) : (
          <>
            <span className={cn("block truncate font-medium text-foreground", compact ? "text-[13px]" : "text-sm")}>{file.name}</span>
            <span className="block text-xs text-muted-foreground">{formatBytes(file.size)}</span>
          </>
        )}
      </span>
      <Button
        type="button"
        size={compact ? "xs" : "sm"}
        variant="outline"
        disabled={disabled}
        onClick={() => inputRef.current?.click()}
        className="shrink-0 cursor-pointer"
      >
        {file === null ? "Choose PDF" : "Change"}
      </Button>
    </div>
  );
}

function CommentField({
  value,
  onChange,
}: {
  readonly value: string;
  readonly onChange: (value: string) => void;
}) {
  return (
    <label className="block space-y-1.5">
      <span className="text-xs font-medium text-muted-foreground">Comment (optional)</span>
      <Textarea
        value={value}
        onChange={(event) => onChange(event.target.value)}
        placeholder="What was done, for whoever reads this later"
        rows={2}
        className="resize-none"
      />
    </label>
  );
}

/**
 * Inspector's proposal as text: its summary and each change word by word.
 * The PDF pages (pdf.js, two downloads) render only on request.
 */
function ProposalSummary({
  organizationId,
  proposal,
  currentVersion,
}: {
  readonly organizationId: string;
  readonly proposal: DocumentChangesView;
  readonly currentVersion: number | null;
}) {
  const [pdf, setPdf] = useState(false);
  return (
    <div className="space-y-3" data-proposal-summary="">
      <p className="text-sm leading-6 text-muted-foreground">{proposal.summary}</p>
      <ol className="divide-y divide-border/60 rounded-lg border border-border/70 bg-card">
        {proposal.edits.map((edit, index) => (
          <li key={`${index}:${edit.section}`} className="space-y-1 px-3.5 py-2.5">
            <p className="flex items-center gap-2 text-xs text-muted-foreground">
              <span className="tabular-nums">{index + 1}</span>
              <span className="min-w-0 flex-1 truncate font-medium text-foreground">{edit.section}</span>
              <span className="shrink-0 tabular-nums">
                {pageLabel(edit)}
                {editKind(edit) === "changed" ? "" : ` · ${editKind(edit)}`}
              </span>
            </p>
            <InlineDiff
              before={edit.beforeText}
              after={edit.afterText}
              className="line-clamp-3 text-[13px] leading-6"
            />
          </li>
        ))}
      </ol>
      {pdf ? (
        <FindingPdfChangesReview
          organizationId={organizationId}
          proposal={proposal}
          currentVersion={currentVersion}
        />
      ) : (
        <Button
          type="button"
          size="sm"
          variant="outline"
          className="cursor-pointer"
          onClick={() => setPdf(true)}
          data-show-pdf-review=""
        >
          <FileSearchIcon className="size-3.5" />
          Show the PDF pages
        </Button>
      )}
    </div>
  );
}

/**
 * Versions of one document, uploaded files and text captures alike, that may
 * resolve the item: for a suggestion, created after it was raised; for a
 * finding, carrying content its assessment did not read (every version when
 * it was never stamped). See `resolutionCandidates`.
 */
function useLaterVersions(
  organizationId: string | null,
  policyId: string,
  scope: Parameters<typeof resolutionCandidates>[1],
) {
  const { snapshot } = usePolicyVersions(organizationId, policyId);
  const { snapshot: library } = usePolicies(organizationId);
  const policy = library?.policies.find((candidate) => candidate.id === policyId);
  const files: VersionCandidate[] = [
    ...(policy
      ? [
          {
            key: `file-${policy.version ?? 1}`,
            version: policy.version ?? 1,
            createdAt: policy.versionUploadedAt ?? policy.createdAt,
            note: policy.versionNote ?? null,
            label: "file" as const,
          },
        ]
      : []),
    ...(policy?.fileVersions ?? []).map((entry) => ({
      key: `file-${entry.version}`,
      version: entry.version,
      createdAt: entry.uploadedAt,
      note: entry.note,
      label: "file" as const,
    })),
  ];
  const texts: VersionCandidate[] = (snapshot?.versions ?? []).map((candidate) => ({
    key: `text-${candidate.id}`,
    version: candidate.version,
    createdAt: candidate.createdAt,
    note: candidate.note,
    label: "text" as const,
  }));
  return {
    policy,
    candidates: resolutionCandidates([...files, ...texts], scope),
  };
}

/** Library documents created after a gap was raised: the ones that may fill it. */
function useDocumentsSince(organizationId: string | null, raisedAt: string) {
  const { snapshot, uploadDocument } = usePolicies(organizationId);
  const raised = Date.parse(raisedAt);
  const candidates = (snapshot?.policies ?? []).filter(
    (policy) => Date.parse(policy.createdAt) > raised,
  );
  return { candidates, uploadDocument };
}

/**
 * One resolution in flight: guards a double submit, reports busy upwards (so
 * the surface cannot close mid-save), keeps the stage line and the error, and
 * closes on success. `work` returns the evidence to record, or throws.
 */
function useResolutionSubmit({
  onResolve,
  onDone,
  onBusyChange,
}: {
  readonly onResolve: (evidence: ResolutionEvidence) => Promise<string | null>;
  readonly onDone: () => void;
  readonly onBusyChange: (busy: boolean) => void;
}) {
  const submitting = useRef(false);
  const [busy, setBusy] = useState(false);
  const [stage, setStage] = useState("Recording decision…");
  const [error, setError] = useState<string | null>(null);
  const run = async (work: () => Promise<ResolutionEvidence>) => {
    if (submitting.current) return;
    submitting.current = true;
    setBusy(true);
    onBusyChange(true);
    setError(null);
    try {
      const evidence = await work();
      setStage("Recording decision…");
      const message = await onResolve(evidence);
      if (message !== null) {
        setError(message);
        return;
      }
      onDone();
    } catch (cause) {
      setError(
        cause instanceof Error ? cause.message : "The resolution could not be saved. Try again.",
      );
    } finally {
      submitting.current = false;
      setBusy(false);
      onBusyChange(false);
    }
  };
  return { busy, stage, setStage, error, setError, run };
}

/**
 * The amendment dialog's body: Inspector's proposal (for a control
 * amendment), an upload, or a version uploaded since the suggestion was raised.
 */
function VersionBody({
  organizationId,
  policyId,
  raisedAt,
  suggestedNote,
  onResolve,
  onClose,
  onBusyChange,
  recommendationId,
}: {
  readonly organizationId: string | null;
  readonly policyId: string;
  /** Only versions created after this qualify. */
  readonly raisedAt: string;
  /** Placeholder for the version note — the thing being fixed. */
  readonly suggestedNote: string;
  readonly onResolve: (evidence: ResolutionEvidence) => Promise<string | null>;
  readonly onClose: () => void;
  readonly onBusyChange: (busy: boolean) => void;
  /** Set for a control amendment: Inspector's proposal is offered first. */
  readonly recommendationId?: string;
}) {
  const { uploadVersion } = usePolicies(organizationId);
  const { policy, candidates } = useLaterVersions(organizationId, policyId, {
    kind: "suggestion",
    raisedAt,
  });
  const {
    proposal: latestProposal,
    loading: proposalLoading,
    unavailable: proposalUnavailable,
    refresh: refreshProposal,
  } = useFindingChanges(organizationId ?? "", policyId, recommendationId ?? "", "recommendation");
  const applyProposal = useApplyProposal(organizationId, policyId);
  const [savedProposal, setSavedProposal] = useState<{
    proposal: DocumentChangesView;
    version: number;
  } | null>(null);
  const proposal = savedProposal?.proposal ?? latestProposal;
  const hasPdfReview = Boolean(recommendationId);
  const [mode, setMode] = useState<Mode>(hasPdfReview ? "proposal" : "upload");
  const [file, setFile] = useState<File | null>(null);
  const [versionNote, setVersionNote] = useState("");
  const [picked, setPicked] = useState("");
  const [comment, setComment] = useState("");
  const { busy, stage, setStage, error, setError, run } = useResolutionSubmit({
    onResolve,
    onDone: onClose,
    onBusyChange,
  });
  const chosen = candidates.some((candidate) => candidate.key === picked)
    ? picked
    : (candidates[0]?.key ?? "");
  const chosenVersion = candidates.find((candidate) => candidate.key === chosen)?.version;
  const currentVersion = policy ? (policy.version ?? 1) : null;
  const nextVersion = (currentVersion ?? 1) + 1;
  const note = comment.trim().length === 0 ? null : comment.trim();

  const [uploadedEvidence, setUploadedEvidence] = useState<{ file: File; version: number } | null>(
    null,
  );
  const uploadedVersion = uploadedEvidence?.file === file ? uploadedEvidence?.version : undefined;
  const [uploadOnce] = useState(() => createResolutionUpload<number>());
  const submit = () =>
    run(async () => {
      if (mode === "proposal") {
        if (!proposal || !organizationId || !hasPdfReview)
          throw new Error("No Inspector proposal is available yet.");
        setStage("Saving Inspector’s PDF…");
        const resolvedVersion = await applyProposal(proposal, currentVersion);
        setSavedProposal({ proposal, version: resolvedVersion });
        return { resolvedVersion, note: proposalDecisionNote(proposal, note) };
      }
      if (mode === "upload") {
        if (file === null) throw new Error("Choose the PDF that carries the change.");
        setStage(uploadedVersion === undefined ? "Uploading PDF…" : "Recording decision…");
        const resolvedVersion = await uploadOnce(file, async () => {
          const uploaded = await uploadVersion(policyId, file, versionNote.trim() || null);
          if (uploaded.error !== null) throw new Error(uploaded.error);
          setUploadedEvidence({ file, version: uploaded.value });
          return uploaded.value;
        });
        return { resolvedVersion, note };
      }
      const candidate = candidates.find((entry) => entry.key === chosen);
      if (!candidate) throw new Error("Choose an available version that carries the change.");
      return { resolvedVersion: candidate.version, note };
    });

  return (
    <>
      <DialogPanel scrollFade={!hasPdfReview} className="space-y-4" aria-busy={busy}>
        <fieldset disabled={busy} className="min-w-0 space-y-4">
          <AnimatedTabs
            value={mode}
            onValueChange={(value) => {
              setMode(value as Mode);
              setError(null);
            }}
          >
            <AnimatedTabsList>
              {hasPdfReview ? (
                <AnimatedTabsTrigger value="proposal">Inspector changes</AnimatedTabsTrigger>
              ) : null}
              <AnimatedTabsTrigger value="upload">
                {uploadedVersion === undefined
                  ? `Upload version ${nextVersion}`
                  : `Uploaded version ${uploadedVersion}`}
              </AnimatedTabsTrigger>
              <AnimatedTabsTrigger value="existing">Already uploaded</AnimatedTabsTrigger>
            </AnimatedTabsList>
            {hasPdfReview ? (
              <AnimatedTabsContent value="proposal" className={PANE_CLASS}>
                {proposalLoading && !savedProposal ? (
                  <FindingChangesSkeleton />
                ) : proposalUnavailable && !savedProposal ? (
                  <div role="alert">
                    <p>Inspector’s changes could not be loaded.</p>
                    <Button variant="outline" size="sm" onClick={refreshProposal}>
                      Retry
                    </Button>
                  </div>
                ) : proposal ? (
                  <>
                    <ProposalSummary
                      organizationId={organizationId ?? ""}
                      proposal={proposal}
                      currentVersion={currentVersion}
                    />
                    {policy &&
                    currentVersion !== proposal.sourceVersion &&
                    savedProposal?.proposal.id !== proposal.id ? (
                      <p role="alert" className="text-sm text-destructive">
                        The policy changed after this proposal. Ask Inspector to update it against
                        the current version.
                      </p>
                    ) : null}
                    <CommentField value={comment} onChange={setComment} />
                  </>
                ) : (
                  <p className="text-sm text-muted-foreground">
                    No Inspector PDF is ready yet. Upload a revised PDF, or select a version that
                    already addresses this item.
                  </p>
                )}
              </AnimatedTabsContent>
            ) : null}
            <AnimatedTabsContent value="upload" className={PANE_CLASS}>
              <FilePicker
                disabled={busy}
                file={file}
                onChange={(next) => {
                  setFile(next);
                  setError(null);
                }}
                hint={
                  policy
                    ? `Replaces ${policy.filename}; the current file stays in the history`
                    : "The new version of the document"
                }
              />
              <label className="block space-y-1.5">
                <span className="text-xs font-medium text-muted-foreground">
                  {uploadedVersion === undefined ? "What changed in it" : "Saved version note"}
                </span>
                <Textarea
                  value={versionNote}
                  disabled={uploadedVersion !== undefined}
                  onChange={(event) => setVersionNote(event.target.value)}
                  placeholder={`e.g. ${suggestedNote}`}
                  rows={2}
                  className="resize-none"
                />
              </label>
              <CommentField value={comment} onChange={setComment} />
            </AnimatedTabsContent>
            <AnimatedTabsContent value="existing" className={PANE_CLASS}>
              {candidates.length === 0 ? (
                <p className="rounded-xl border border-dashed border-border bg-muted/20 px-4 py-3 text-sm text-muted-foreground">
                  No version has been uploaded since this item was raised. Upload a new version to
                  record the change.
                </p>
              ) : (
                <div className="space-y-1.5">
                  <span className="text-xs font-medium text-muted-foreground">Resolved in</span>
                  <Select value={chosen} onValueChange={(value) => setPicked(value ?? "")}>
                    <SelectTrigger
                      size="sm"
                      className="w-full"
                      aria-label="Version that carried the change"
                    >
                      <span className="truncate">
                        {chosenVersion === undefined ? "Choose a version" : `v${chosenVersion}`}
                      </span>
                    </SelectTrigger>
                    <SelectContent>
                      {candidates.map((candidate) => (
                        <SelectItem key={candidate.key} value={candidate.key}>
                          v{candidate.version} ({candidate.label}) ·{" "}
                          {formatDate(candidate.createdAt)}
                          {candidate.note ? ` — ${candidate.note}` : ""}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
              <CommentField value={comment} onChange={setComment} />
            </AnimatedTabsContent>
          </AnimatedTabs>
        </fieldset>
      </DialogPanel>
      <DialogFooter className="flex-col sm:flex-col">
        {error !== null ? (
          <p
            role="alert"
            className="rounded-lg border border-destructive/20 bg-destructive/5 px-3 py-2 text-sm text-destructive"
          >
            {error}
          </p>
        ) : busy ? (
          <p role="status" className="text-xs text-muted-foreground">
            {stage}
          </p>
        ) : null}
        <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
          <DialogClose
            render={
              <Button
                type="button"
                size="sm"
                variant="ghost"
                disabled={busy}
                className="cursor-pointer"
              >
                Cancel
              </Button>
            }
          />
          <Button
            type="button"
            size="sm"
            onClick={() => void submit()}
            disabled={
              busy ||
              (mode === "proposal"
                ? !proposal ||
                  (proposalUnavailable && !savedProposal) ||
                  (savedProposal?.proposal.id !== proposal.id &&
                    currentVersion !== proposal.sourceVersion)
                : mode === "upload"
                  ? file === null
                  : chosen.length === 0)
            }
            className="cursor-pointer gap-1.5"
          >
            {mode === "upload" ? <FileUpIcon className="size-3.5" /> : null}
            {busy
              ? stage
              : mode === "proposal"
                ? savedProposal && savedProposal.proposal.id === proposal?.id
                  ? `Resolve in v${savedProposal?.version}`
                  : "Use Inspector changes and resolve"
                : mode === "upload"
                  ? uploadedVersion === undefined
                    ? `Upload v${nextVersion} and resolve`
                    : `Resolve in v${uploadedVersion}`
                  : `Resolve in v${chosenVersion}`}
          </Button>
        </div>
      </DialogFooter>
    </>
  );
}

function MissingDocumentBody({
  organizationId,
  recommendation,
  onResolve,
  onClose,
  onBusyChange,
}: {
  readonly organizationId: string | null;
  readonly recommendation: Recommendation;
  readonly onResolve: (evidence: ResolutionEvidence) => Promise<string | null>;
  readonly onClose: () => void;
  readonly onBusyChange: (busy: boolean) => void;
}) {
  const { candidates, uploadDocument } = useDocumentsSince(organizationId, recommendation.createdAt);
  // Upload first: the document changing is the point, naming a version is
  // the exception for when it already did.
  const [mode, setMode] = useState<Mode>("upload");
  const [file, setFile] = useState<File | null>(null);
  const [picked, setPicked] = useState("");
  const [comment, setComment] = useState("");
  const { busy, stage, setStage, error, setError, run } = useResolutionSubmit({
    onResolve,
    onDone: onClose,
    onBusyChange,
  });
  const chosen = candidates.some((candidate) => candidate.id === picked)
    ? picked
    : (candidates[0]?.id ?? "");
  const chosenPolicy = candidates.find((policy) => policy.id === chosen);
  const note = comment.trim().length === 0 ? null : comment.trim();

  const [uploadedFile, setUploadedFile] = useState<File | null>(null);
  const [uploadOnce] = useState(() => createResolutionUpload<string>());
  const submit = () =>
    run(async () => {
      if (mode === "upload") {
        if (file === null) throw new Error("Choose the PDF of the document.");
        setStage(uploadedFile === file ? "Recording decision…" : "Uploading PDF…");
        const resolvedPolicyId = await uploadOnce(file, async () => {
          const uploaded = await uploadDocument({ file, jurisdictionSlugs: [] });
          if (uploaded.error !== null) throw new Error(uploaded.error);
          setUploadedFile(file);
          return uploaded.value.id;
        });
        return { resolvedPolicyId, note };
      }
      if (!candidates.some((candidate) => candidate.id === chosen)) {
        throw new Error("Choose an available document that fills the gap.");
      }
      return { resolvedPolicyId: chosen, note };
    });

  return (
    <>
      <DialogPanel className="space-y-4" aria-busy={busy}>
        <fieldset disabled={busy} className="min-w-0 space-y-4">
          <AnimatedTabs
            value={mode}
            onValueChange={(value) => {
              setMode(value as Mode);
              setError(null);
            }}
          >
            <AnimatedTabsList>
              <AnimatedTabsTrigger value="upload">Upload the document</AnimatedTabsTrigger>
              <AnimatedTabsTrigger value="existing">Already uploaded</AnimatedTabsTrigger>
            </AnimatedTabsList>
            <AnimatedTabsContent value="upload" className={PANE_CLASS}>
              <FilePicker
                disabled={busy}
                file={file}
                onChange={(next) => {
                  setFile(next);
                  setError(null);
                }}
                hint="Goes into the library like any upload; jurisdictions are detected from the text"
              />
              <CommentField value={comment} onChange={setComment} />
            </AnimatedTabsContent>
            <AnimatedTabsContent value="existing" className={PANE_CLASS}>
              {candidates.length === 0 ? (
                <p className="rounded-xl border border-dashed border-border bg-muted/20 px-4 py-3 text-sm text-muted-foreground">
                  Nothing has been uploaded since this gap was raised. Upload the document instead.
                </p>
              ) : (
                <div className="space-y-1.5">
                  <span className="text-xs font-medium text-muted-foreground">Resolved by</span>
                  <Select value={chosen} onValueChange={(value) => setPicked(value ?? "")}>
                    <SelectTrigger
                      size="sm"
                      className="w-full"
                      aria-label="Document that fills the gap"
                    >
                      <span className="truncate">
                        {chosenPolicy ? policyDisplayName(chosenPolicy) : "Choose a document"}
                      </span>
                    </SelectTrigger>
                    <SelectContent>
                      {candidates.map((policy) => (
                        <SelectItem key={policy.id} value={policy.id}>
                          {policyDisplayName(policy)} · {formatDate(policy.createdAt)}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
              <CommentField value={comment} onChange={setComment} />
            </AnimatedTabsContent>
          </AnimatedTabs>
        </fieldset>
        {busy ? (
          <p role="status" className="text-xs text-muted-foreground">
            {stage}
          </p>
        ) : null}
        {error !== null && (
          <p
            role="alert"
            className="rounded-lg border border-destructive/20 bg-destructive/5 px-3 py-2 text-sm text-destructive"
          >
            {error}
          </p>
        )}
      </DialogPanel>
      <DialogFooter>
        <DialogClose
          render={
            <Button
              type="button"
              size="sm"
              variant="ghost"
              disabled={busy}
              className="cursor-pointer"
            >
              Cancel
            </Button>
          }
        />
        <Button
          type="button"
          size="sm"
          onClick={() => void submit()}
          disabled={busy || (mode === "upload" ? file === null : chosen.length === 0)}
          className="cursor-pointer gap-1.5"
        >
          {mode === "upload" ? <FileUpIcon className="size-3.5" /> : null}
          {busy
            ? stage
            : mode === "upload"
              ? uploadedFile !== null && uploadedFile === file
                ? "Retry resolution"
                : "Upload and resolve"
              : "Resolve"}
        </Button>
      </DialogFooter>
    </>
  );
}

export function ResolveRecommendationDialog({
  organizationId,
  recommendation,
  open,
  onOpenChange,
  onResolve,
}: {
  readonly organizationId: string | null;
  readonly recommendation: Recommendation | null;
  readonly open: boolean;
  readonly onOpenChange: (open: boolean) => void;
  readonly onResolve: (evidence: ResolutionEvidence) => Promise<string | null>;
}) {
  const [busy, setBusy] = useState(false);
  if (recommendation === null) return null;
  const amendment = recommendation.kind !== "missing_document";
  const pdfReview = recommendation.kind === "control_amendment";
  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!busy) onOpenChange(next);
      }}
    >
      <DialogPopup
        bottomStickOnMobile={!pdfReview}
        className={pdfReview ? VERSION_DIALOG_CLASS : "max-w-lg"}
      >
        <DialogHeader>
          <DialogTitle>
            {amendment ? "Resolve the amendment" : "Resolve the missing document"}
          </DialogTitle>
          <DialogDescription>
            {pdfReview
              ? "Review Inspector’s PDF, upload a revision, or name a version uploaded since this suggestion was raised. Approval saves the version and records your decision."
              : amendment
                ? "Upload the version that carries the change, or name one uploaded since this suggestion was raised."
                : "A gap is resolved by the document existing: upload it, or name one uploaded since the gap was raised."}
          </DialogDescription>
        </DialogHeader>
        {/* Keyed so a different recommendation starts the form afresh. */}
        {amendment && recommendation.policyId !== null ? (
          <VersionBody
            key={recommendation.id}
            {...(pdfReview ? { recommendationId: recommendation.id } : {})}
            organizationId={organizationId}
            policyId={recommendation.policyId}
            raisedAt={recommendation.createdAt}
            suggestedNote={recommendation.title}
            onResolve={onResolve}
            onBusyChange={setBusy}
            onClose={() => onOpenChange(false)}
          />
        ) : (
          <MissingDocumentBody
            key={recommendation.id}
            organizationId={organizationId}
            recommendation={recommendation}
            onResolve={onResolve}
            onBusyChange={setBusy}
            onClose={() => onOpenChange(false)}
          />
        )}
      </DialogPopup>
    </Dialog>
  );
}

/** What the finding page's resolve form is resolving. */
export interface ResolveSubject {
  readonly target: ResolveTarget;
  readonly id: string;
  /** Null for a missing document: there is no document yet. */
  readonly policyId: string | null;
  /** For an amendment or a missing document, only later evidence qualifies. */
  readonly raisedAt: string;
  readonly documentName: string;
  /** Shown at the top, so the decision is made against the rule. */
  readonly requirement: ReadonlyArray<string>;
  /** A finding's assessment stamp; when known, only newer versions qualify. */
  readonly assessed?: AssessedStamp;
}

interface ResolveFormProps {
  readonly organizationId: string | null;
  readonly subject: ResolveSubject;
  /** Opened from a "resolve another way" menu item. */
  readonly initialChoice?: ResolveChoice | undefined;
  readonly onResolve: (evidence: ResolutionEvidence) => Promise<string | null>;
  readonly onClose: () => void;
  readonly onBusyChange: (busy: boolean) => void;
}

/** One way to resolve, as a radio row; its control opens under it once picked. */
function ChoiceRow({
  value,
  selected,
  title,
  description,
  disabled = false,
  children,
}: {
  readonly value: ResolveChoice;
  readonly selected: boolean;
  readonly title: string;
  readonly description: string;
  readonly disabled?: boolean;
  readonly children?: ReactNode;
}) {
  return (
    <div
      className={cn(
        "rounded-lg border transition-colors duration-150",
        selected ? "border-foreground/20 bg-foreground/[0.035]" : "border-border/70",
        disabled && "opacity-60",
      )}
      data-resolve-choice={value}
      data-selected={selected ? "" : undefined}
    >
      <label className={cn("flex items-start gap-2.5 px-3 py-2", disabled ? "cursor-not-allowed" : "cursor-pointer")}>
        <Radio value={value} disabled={disabled} className="mt-0.5" />
        <span className="min-w-0">
          <span className="block text-[13px] font-medium leading-5">{title}</span>
          <span className="block text-xs leading-5 text-muted-foreground">{description}</span>
        </span>
      </label>
      {selected && children ? <div className="px-3 pb-3 ps-9">{children}</div> : null}
    </div>
  );
}

/**
 * The frame every resolve form shares: the requirement, the choices, one
 * note, and Cancel beside a confirm button that names what it records.
 */
function ResolveFrame({
  subject,
  documentLine,
  choice,
  onChoice,
  children,
  note,
  onNote,
  noteLabel,
  notePlaceholder,
  noteHint,
  blocker,
  busy,
  stage,
  error,
  confirmLabel,
  onConfirm,
  onClose,
}: {
  readonly subject: ResolveSubject;
  readonly documentLine: string;
  readonly choice: ResolveChoice;
  readonly onChoice: (choice: ResolveChoice) => void;
  readonly children: ReactNode;
  readonly note: string;
  readonly onNote: (note: string) => void;
  readonly noteLabel: string;
  readonly notePlaceholder: string;
  /** Beside a required note's label: how much is missing, or "Good". */
  readonly noteHint: { readonly text: string; readonly good: boolean } | null;
  /** Why the confirm is not ready; it stays disabled until this is null. */
  readonly blocker: string | null;
  readonly busy: boolean;
  readonly stage: string;
  readonly error: string | null;
  readonly confirmLabel: string;
  readonly onConfirm: () => void;
  readonly onClose: () => void;
}) {
  const [first, ...rest] = subject.requirement;
  return (
    <div className="space-y-3" aria-busy={busy} data-resolve-form={subject.target}>
      <div>
        <p className="text-[13px] font-semibold">
          {subject.target === "missing" ? "Resolve the missing document" : "Resolve finding"}
        </p>
        <p className="truncate text-xs text-muted-foreground">{documentLine}</p>
      </div>
      {first ? (
        <div className="rounded-md bg-muted/50 px-3 py-2 text-xs leading-5" data-resolve-requirement="">
          <p className="font-medium text-muted-foreground">Requirement</p>
          <p className="line-clamp-3 text-foreground/90" title={first}>
            {first}
          </p>
          {rest.length > 0 ? (
            <p className="text-muted-foreground">
              and {rest.length} more {rest.length === 1 ? "change" : "changes"}
            </p>
          ) : null}
        </div>
      ) : null}
      <fieldset disabled={busy} className="min-w-0 space-y-3">
        <RadioGroup
          value={choice}
          onValueChange={(value) => onChoice(value as ResolveChoice)}
          aria-label="How it is resolved"
          className="gap-1.5"
        >
          {children}
        </RadioGroup>
        <label className="block space-y-1">
          <span className="flex items-center justify-between text-xs font-medium text-muted-foreground">
            <span>{noteLabel}</span>
            {noteHint ? (
              <span className={noteHint.good ? "text-success" : undefined} data-resolve-note-hint="">
                {noteHint.text}
              </span>
            ) : null}
          </span>
          <Textarea
            size="sm"
            value={note}
            onChange={(event) => onNote(event.target.value)}
            placeholder={notePlaceholder}
            rows={2}
            className="[&_textarea]:resize-none"
            data-resolve-note=""
          />
        </label>
      </fieldset>
      {error !== null ? (
        <p role="alert" className="rounded-md border border-destructive/20 bg-destructive/5 px-2.5 py-1.5 text-xs text-destructive">
          {error}
        </p>
      ) : busy ? (
        <p role="status" className="sr-only">
          {stage}
        </p>
      ) : null}
      <div className="flex justify-end gap-2">
        <Button type="button" size="sm" variant="ghost" disabled={busy} onClick={onClose}>
          Cancel
        </Button>
        <Button
          type="button"
          size="sm"
          disabled={busy || blocker !== null}
          onClick={onConfirm}
          data-resolve-confirm=""
        >
          {busy ? <LoaderCircleIcon aria-hidden className="animate-spin" /> : null}
          {busy ? stage : confirmLabel}
        </Button>
      </div>
    </div>
  );
}

/** A regulatory finding or an amendment: closes against a version of its document. */
function VersionResolveForm({
  organizationId,
  subject,
  policyId,
  initialChoice,
  onResolve,
  onClose,
  onBusyChange,
}: ResolveFormProps & { readonly policyId: string }) {
  const finding = subject.target === "finding";
  const { uploadVersion } = usePolicies(organizationId);
  const { policy, candidates } = useLaterVersions(
    organizationId,
    policyId,
    finding ? { kind: "finding", assessed: subject.assessed } : { kind: "suggestion", raisedAt: subject.raisedAt },
  );
  // "Newer than the assessed version" only means something with a stamp.
  const stamped = finding && assessmentBaselineKnown(subject.assessed);
  const choices = resolveChoices(subject.target, { stamped });
  const [picked, setPicked] = useState<ResolveChoice | null>(
    initialChoice && choices.includes(initialChoice) ? initialChoice : null,
  );
  // A menu's "newer version" with none to name falls back to the default.
  const choice =
    picked !== null && !(picked === "newer" && candidates.length === 0)
      ? picked
      : defaultResolveChoice(choices, { versions: candidates.length, documents: 0 });
  const [pickedVersion, setPickedVersion] = useState("");
  const [file, setFile] = useState<File | null>(null);
  const [note, setNote] = useState("");
  const { busy, stage, setStage, error, setError, run } = useResolutionSubmit({
    onResolve,
    onDone: onClose,
    onBusyChange,
  });
  const chosen = candidates.find((candidate) => candidate.key === pickedVersion) ?? candidates[0];
  const currentVersion = policy ? (policy.version ?? 1) : null;
  const coveredProblem = coveredNoteProblem(note);
  const [uploadedEvidence, setUploadedEvidence] = useState<{ file: File; version: number } | null>(null);
  const uploadedVersion = uploadedEvidence?.file === file ? uploadedEvidence?.version : undefined;
  const [uploadOnce] = useState(() => createResolutionUpload<number>());
  const trimmed = note.trim().length === 0 ? null : note.trim();
  const blocker = resolveBlocker(choice, {
    versions: candidates.length,
    documents: 0,
    hasFile: file !== null,
    currentVersion,
    coveredNote: note,
  });
  const submit = () =>
    run(async () => {
      if (choice === "upload") {
        if (file === null) throw new Error("Choose the PDF that carries the change.");
        setStage(uploadedVersion === undefined ? "Uploading PDF…" : "Recording decision…");
        // The note is the new version's note and the decision's.
        const resolvedVersion = await uploadOnce(file, async () => {
          const uploaded = await uploadVersion(policyId, file, trimmed);
          if (uploaded.error !== null) throw new Error(uploaded.error);
          setUploadedEvidence({ file, version: uploaded.value });
          return uploaded.value;
        });
        return { resolvedVersion, note: trimmed };
      }
      if (choice === "covered") {
        if (currentVersion === null) throw new Error("The document's current version is not loaded yet.");
        if (coveredProblem !== null) throw new Error(coveredProblem);
        return { resolvedVersion: currentVersion, note: note.trim() };
      }
      if (!chosen) throw new Error("Choose an available version that carries the change.");
      return { resolvedVersion: chosen.version, note: trimmed };
    });
  const assessed = subject.assessed?.assessedVersion ?? null;
  return (
    <ResolveFrame
      subject={subject}
      documentLine={[subject.documentName, currentVersion === null ? null : `File v${currentVersion}`, stamped && assessed !== null ? `assessed v${assessed}` : null]
        .filter(Boolean)
        .join(" · ")}
      choice={choice}
      onChoice={(next) => {
        setPicked(next);
        setError(null);
      }}
      note={note}
      onNote={(next) => {
        setNote(next);
        setError(null);
      }}
      noteLabel={choice === "covered" ? `How File v${currentVersion ?? "?"} covers it` : "Note (optional)"}
      notePlaceholder={
        choice === "covered"
          ? "e.g. Section 4.2 already names the reporting owner"
          : choice === "upload"
            ? "What changed in it, for whoever reads this later"
            : "What was done, for whoever reads this later"
      }
      noteHint={
        choice !== "covered" || note.trim().length === 0
          ? null
          : blocker === null
            ? { text: "Good", good: true }
            : { text: blocker, good: false }
      }
      blocker={blocker}
      busy={busy}
      stage={stage}
      error={error}
      confirmLabel={resolveConfirmLabel(choice, {
        picked: chosen ? versionName(chosen) : null,
        currentVersion,
        documentName: null,
        uploadedVersion,
      })}
      onConfirm={() => void submit()}
      onClose={onClose}
    >
      <ChoiceRow
        value="newer"
        selected={choice === "newer"}
        disabled={candidates.length === 0}
        title={stamped || !finding ? "Fixed in a newer version" : "Fixed in a version"}
        description={
          candidates.length === 0
            ? finding
              ? "No version is newer than the one assessed."
              : "No version since this was raised."
            : `${candidates.length} ${candidates.length === 1 ? "version" : "versions"} to choose from. No upload needed.`
        }
      >
        <Select value={chosen?.key ?? ""} onValueChange={(value) => setPickedVersion(value ?? "")}>
          <SelectTrigger size="sm" className="w-full" aria-label="Version that carried the change">
            <span className="truncate">{chosen ? versionName(chosen) : "Choose a version"}</span>
          </SelectTrigger>
          <SelectContent>
            {candidates.map((candidate) => (
              <SelectItem key={candidate.key} value={candidate.key}>
                {versionName(candidate)} · {formatDate(candidate.createdAt)}
                {candidate.note ? ` — ${candidate.note}` : ""}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </ChoiceRow>
      {choices.includes("covered") ? (
        <ChoiceRow
          value="covered"
          selected={choice === "covered"}
          disabled={currentVersion === null}
          title={`Already covered by File v${currentVersion ?? "?"}`}
          description="The current version answers it. Say where in a note."
        />
      ) : null}
      <ChoiceRow
        value="upload"
        selected={choice === "upload"}
        title="Upload a new version"
        description={`Saves File v${(currentVersion ?? 0) + 1} and resolves with it.`}
      >
        <FilePicker
          compact
          disabled={busy}
          file={file}
          onChange={(next) => {
            setFile(next);
            setError(null);
          }}
          hint={policy ? `Replaces ${policy.filename}; it stays in the history` : "The new version of the document"}
        />
      </ChoiceRow>
    </ResolveFrame>
  );
}

/** A missing document: closes when the document exists, uploaded here or since. */
function DocumentResolveForm({ organizationId, subject, initialChoice, onResolve, onClose, onBusyChange }: ResolveFormProps) {
  const { candidates, uploadDocument } = useDocumentsSince(organizationId, subject.raisedAt);
  const choices = resolveChoices("missing", { stamped: false });
  const [picked, setPicked] = useState<ResolveChoice | null>(
    initialChoice && choices.includes(initialChoice) ? initialChoice : null,
  );
  const choice =
    picked !== null && !(picked === "document" && candidates.length === 0)
      ? picked
      : defaultResolveChoice(choices, { versions: 0, documents: candidates.length });
  const [pickedDocument, setPickedDocument] = useState("");
  const [file, setFile] = useState<File | null>(null);
  const [note, setNote] = useState("");
  const { busy, stage, setStage, error, setError, run } = useResolutionSubmit({
    onResolve,
    onDone: onClose,
    onBusyChange,
  });
  const chosen = candidates.find((policy) => policy.id === pickedDocument) ?? candidates[0];
  const [uploadedFile, setUploadedFile] = useState<File | null>(null);
  const [uploadOnce] = useState(() => createResolutionUpload<string>());
  const trimmed = note.trim().length === 0 ? null : note.trim();
  const submit = () =>
    run(async () => {
      if (choice === "upload-document") {
        if (file === null) throw new Error("Choose the PDF of the document.");
        setStage(uploadedFile === file ? "Recording decision…" : "Uploading PDF…");
        const resolvedPolicyId = await uploadOnce(file, async () => {
          const uploaded = await uploadDocument({ file, jurisdictionSlugs: [] });
          if (uploaded.error !== null) throw new Error(uploaded.error);
          setUploadedFile(file);
          return uploaded.value.id;
        });
        return { resolvedPolicyId, note: trimmed };
      }
      if (!chosen) throw new Error("Choose an available document that fills the gap.");
      return { resolvedPolicyId: chosen.id, note: trimmed };
    });
  return (
    <ResolveFrame
      subject={subject}
      documentLine={subject.documentName}
      choice={choice}
      onChoice={(next) => {
        setPicked(next);
        setError(null);
      }}
      note={note}
      onNote={setNote}
      noteLabel="Note (optional)"
      notePlaceholder="What was done, for whoever reads this later"
      noteHint={null}
      blocker={resolveBlocker(choice, {
        versions: 0,
        documents: candidates.length,
        hasFile: file !== null,
        currentVersion: null,
        coveredNote: "",
      })}
      busy={busy}
      stage={stage}
      error={error}
      confirmLabel={resolveConfirmLabel(choice, {
        picked: null,
        currentVersion: null,
        documentName: chosen ? policyDisplayName(chosen) : null,
      })}
      onConfirm={() => void submit()}
      onClose={onClose}
    >
      <ChoiceRow
        value="document"
        selected={choice === "document"}
        disabled={candidates.length === 0}
        title="Already in the library"
        description={
          candidates.length === 0
            ? "Nothing has been uploaded since this gap was raised."
            : "Pick the document uploaded since this gap was raised."
        }
      >
        <Select value={chosen?.id ?? ""} onValueChange={(value) => setPickedDocument(value ?? "")}>
          <SelectTrigger size="sm" className="w-full" aria-label="Document that fills the gap">
            <span className="truncate">{chosen ? policyDisplayName(chosen) : "Choose a document"}</span>
          </SelectTrigger>
          <SelectContent>
            {candidates.map((policy) => (
              <SelectItem key={policy.id} value={policy.id}>
                {policyDisplayName(policy)} · {formatDate(policy.createdAt)}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </ChoiceRow>
      <ChoiceRow
        value="upload-document"
        selected={choice === "upload-document"}
        title="Upload the document"
        description="Adds it to the library and resolves with it."
      >
        <FilePicker
          compact
          disabled={busy}
          file={file}
          onChange={(next) => {
            setFile(next);
            setError(null);
          }}
          hint="Jurisdictions are detected from the text"
        />
      </ChoiceRow>
    </ResolveFrame>
  );
}

/**
 * The finding page's resolve form (it sits in a popover under the top bar).
 * Keyed by the caller per item, so a different finding starts afresh.
 */
export function ResolveFindingForm(props: ResolveFormProps) {
  const { subject } = props;
  return subject.target === "missing" || subject.policyId === null ? (
    <DocumentResolveForm {...props} />
  ) : (
    <VersionResolveForm {...props} policyId={subject.policyId} />
  );
}
